<template>
  <div>
    <CostMasterModal
      v-if="!loading && masterModal"
      ref="master-modal"
      :cost-id="parseInt(costId)"
      :stage="masterModal"
      :emit-only="isControlPanel"
      @close="closeCostsModal"
      @cost-edited="onCostEdited"
    />

    <MassUploadCostsModal 
      v-if="openMassUploadModal"
      @close="openMassUploadModal = false"
      @upload-finished="refreshCostList"
      @new-suppliers-detected="onNewSuppliersDetected"
    />

    <AddSupplierListModal
      v-if="showAddSupplierModal"
      @close="closeAddSupplierModal"
      @created="refreshCostList"
      :supplierList="newSupplierList"
    />

    <TitleBar>
      <template v-slot:title><translate>Costs</translate></template>
      <template v-slot:buttons>
        <!-- <OnboardingIntroLink :onboarding-intro="'onboarding_web_costs'" /> -->

        <GekkoButton 
          v-if="!isControlPanel"
          :text="$gettext('New')" 
          icon="fa-solid fa-plus"
          :to="Urls['costs_add_cost']()"
          class="btn mr-2"
        />

        <GekkoButton 
          v-if="isControlPanel"
          :text="$gettext('New')" 
          icon="fa-solid fa-plus"
          :to="{ name: 'ControlPanelAddCost', params: { customerId: getCompanyId } }"
          class="btn mr-2"
        />

        <b-dropdown class="icon-toggle ui-dashboard-menu-dropdown" variant="outline-primary" right no-caret>
          <template #button-content>
            <font-awesome-icon icon="fa-solid fa-ellipsis-vertical" />
          </template>
          <b-dropdown-item @click="exportCosts('csv')" v-b-tooltip.hover.right :title="tooltipTextXls">
            <font-awesome-icon icon="fa-regular fa-file" /><span v-translate>Download as excel file (.xls)</span>
          </b-dropdown-item>
          <b-dropdown-item @click="exportCosts('zip')" class="act" v-b-tooltip.hover.right :title="tooltipTextZip">
            <font-awesome-icon icon="fa-regular fa-file-zipper" /><span v-translate>Download as xls + images (.zip)</span>
          </b-dropdown-item>
          <b-dropdown-item @click="openMassUploadModal = true" class="act premium-badge--inline" v-b-tooltip.hover.right :title="tooltipTextBatchUpload">
            <font-awesome-icon icon="fa-solid fa-upload" />
            <span v-translate>Batch upload</span>
            <GkBadge 
              icon="fa-solid fa-crown"
              icon-only
              type="premium"
            />
          </b-dropdown-item>
        </b-dropdown>

      </template>
    </TitleBar>
    <div class="content">
      <CostTable :excludeTableFields="['is_invoiced']" />
    </div>
  </div>
</template>
<script>
import { mapGetters, mapActions } from "vuex"

import TitleBar from "@/components/TitleBar"
import CostTable from "@/components/costs/CostTable"
import GekkoButton from "@/components/GekkoButton"
import CostService from "@/store/services/cost.service.js"
// import OnboardingIntroLink from "@/components/ui/OnboardingIntroLink"
import CostMasterModal from "@/components/costs/CostMasterModal"
import MassUploadCostsModal from "@/components/costs/ui/MassUploadCostsModal"
import AddSupplierListModal from '@/components/costs/ui/AddSupplierListModal.vue'
import GkBadge from "@/components/GkBadge"

export default {
  name: "Costs",
  components: {
    TitleBar,
    CostTable,
    GekkoButton,
    // OnboardingIntroLink,
    CostMasterModal,
    MassUploadCostsModal,
    AddSupplierListModal,
    GkBadge
  },
  data () {
    return {
      tooltipTextZip: this.$gettext("Download current selection as xls, and include the proof images, as ZIP archive."),
      tooltipTextXls: this.$gettext("Download current selection as xls only."),
      tooltipTextBatchUpload: this.$gettext("Upload multiple images or PDFs at once and have them scanned with AI to create costs with pre-filled data."),
      loading: true,
      masterModal: null,
      costId: null,
      openMassUploadModal: false,
      showAddSupplierModal: false,
      newSupplierList: null
    }
  },
  computed:{
    ...mapGetters ({
      getCompanyId: 'main/getCompanyId'
    }),
    isControlPanel () {
      return this.$route.path.includes('control-panel')
    }
  },
  watch: {
    $route: {
      immediate: true,
      handler (to) {
        if (!this.isControlPanel) {
          this.masterModal = to.meta.masterModal
        } else {
          if (this.$route.name === 'ControlPanelEditCost') this.masterModal = 'edit'
          else if (this.$route.name === 'ControlPanelAddCost') this.masterModal = 'add'
          else {
            this.masterModal = null
          }
        }
        this.costId = this.$route.params['costId']
        if (this.masterModal) {
          document.body.classList.add('master-modal-on')
        } else {
          document.body.classList.remove('master-modal-on')
        }
      }
    },
  },
  created () {
    this.loadCostsGlobalData().then(() => {
      this.loading = false
    })
  },
  methods: {
    ...mapActions ({
      loadCostsGlobalData: 'costs/loadCostsGlobalData'
    }),
    exportCosts (exportFormat) {
      let filters = this.$store.getters['filterStore/getFilter'];
      if (this.isControlPanel) {
        filters.control_panel = true;
      }
      filters.owner_company = this.getCompanyId;
      delete filters.option;

    if (exportFormat === "zip") {
        CostService.downloadCostsZip(filters)
          .then(()=>{
            const alert_text = this.$gettext('Your download is being prepared, check your downloads page to download the file.')
            this.$store.commit('setAlert', {title: this.$gettext('Cost Download'), text:alert_text, variant:'info',duration:4});
            if (this.$route.name === "ControlPanelCosts") {
              this.$router.push({ name: 'ControlPanelFiles' })
            } else {
              this.$router.push({ name: 'Files' })
            }
          })
         .catch(error => {
            if (error.status === 429) {
              const alert_text = this.$gettext('You\'ve reached your download limit for today.')
              this.$store.commit('setAlert', {title: this.$gettext('Cost Download'), text:alert_text, variant:'danger',duration:4});
              this.$store.commit('togglePremiumModal', 'web_files_download_limit')
            }
          });
      }else{
        CostService.exportCSV(window.Urls['v3:cost_export_costs'](), filters)
          .then(_ => {
            const alert_text = this.$gettext('Your download is being prepared, check your downloads page to download the file.')
            this.$store.commit('setAlert', {title: this.$gettext('Cost Download'), text:alert_text, variant:'info',duration:4});
            if (this.$route.name === "ControlPanelCosts") {
              this.$router.push({ name: 'ControlPanelFiles' })
            } else {
              this.$router.push({ name: 'Files' })
            }
          })
          .catch(error => {
            if (error.status === 429) {
              const alert_text = this.$gettext('You\'ve reached your download limit for today.')
              this.$store.commit('setAlert', {title: this.$gettext('Cost Download'), text:alert_text, variant:'danger',duration:4});
              this.$store.commit('togglePremiumModal', 'web_files_download_limit')
            }
          });
      }
    },
    closeCostsModal () {
      this.masterModal = null
      if (this.isControlPanel) {
        this.$router.push({ name: 'ControlPanelCosts' }).catch(() => {})
      } else {
        this.$router.push({ name: 'Costs' }).catch(() => {})
      }
    },
    onCostEdited () {
      // TODO this is a bad solution for control-panel, should have it's own view file (~ControlPanelCosts)
      if (this.isControlPanel) {
        this.$store.commit('costs/REFRESH_COST_LIST')
        this.$router.push({ name: 'ControlPanelCosts' }).catch(() => {})
      }
    },
    refreshCostList() {
      this.$store.commit('costs/REFRESH_COST_LIST')
    },
    onNewSuppliersDetected(newSuppliers) {
      this.newSupplierList = newSuppliers
      this.showAddSupplierModal = true
    },
    closeAddSupplierModal() {
      this.showAddSupplierModal = false
      this.newSupplierList = null
    }
  }
}
</script>
<style scoped>

</style>